// Core
import { useCallback, useState } from 'react';
import { useQuery } from 'react-query';

// Chakra
import {
  Text,
  useColorModeValue,
  Divider,
  Flex,
  Table,
  Thead,
  Tr,
  Td,
  Tbody,
  InputGroup,
  InputLeftElement,
  Input,
  FormControl,
} from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';

// Components
import NotFound from 'views/Dashboard/404.js';
import Pagination from '../../../components/Pagination';
import { useHistory } from 'react-router-dom';
import Card from '../../../components/Card/Card';
import TableSkeleton from '../../../components/TableSkeleton';
import { parseDateTime } from '../../../services/Utils';
import UserPurchasesStats from './components/UserPurchasesStats';
import { useDebounce } from 'use-debounce';
import { SearchIcon } from '@chakra-ui/icons';
import usePermissionStatusByKey from '../../../hooks/usePermissionStatusByKey';

const UserPurchase = () => {
  const history = useHistory();
  const borderColor = useColorModeValue('blue.50', 'rgba(255, 255, 255, 0.10)');

  const textColor = useColorModeValue('gray.700', 'white');
  const paddingTd = '16px 0 16px 24px';
  const [offset, setOffset] = useState(0);
  const [userPurchases, setUserPurchases] = useState([]);
  const [userPurchasesMeta, setUserPurchasesMeta] = useState([]);
  const limit = 25;

  const [search, setSearch] = useState();
  const [debouncedSearch] = useDebounce(search, 500);

  const isUserPurchases = usePermissionStatusByKey('GET:/admin/user-purchases');
  const isUserPurchasesStats = usePermissionStatusByKey(
    'GET:/admin/user-purchases/stats',
  );
  const isUserPurchase = usePermissionStatusByKey(
    'GET:/admin/user-purchases/:id',
  );

  const { isFetching, refetch } = useQuery({
    queryKey: ['user-purchases', offset, debouncedSearch, isUserPurchases],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (isUserPurchases) {
        const params = {
          limit,
          offset,
        };

        if (debouncedSearch) {
          params.userCid = debouncedSearch;
        }

        const response = await apiService.getUserPurchases(params);
        setUserPurchases(response.data);
        setUserPurchasesMeta(response.meta);
      }
      return true;
    },
    retry: false,
    enabled: !!isUserPurchases,
    refetchInterval: 30000,
  });

  const handleEditOpen = useCallback(
    (plan) => {
      history.push(`/user-purchases/${plan.id}`);
    },
    [history],
  );

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center">
        <Text
          fontSize="40px"
          fontStyle="normal"
          fontWeight="700"
          color={textColor}
          mb="0px"
          className="page-name"
        >
          User Purchases
        </Text>
      </Flex>

      <Divider
        opacity="1"
        mr="2px"
        borderColor={borderColor}
        borderTopWidth="1px"
        borderBottomWidth="0"
        mb="32px"
      />

      {isUserPurchasesStats && <UserPurchasesStats />}

      {isUserPurchases && (
        <>
          <FormControl
            variant="search"
            maxW="274px"
            py={2}
            className={search?.length > 0 ? 'search-active' : ''}
          >
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon fontSize="24px" ml="8px" />
              </InputLeftElement>
              <Input
                type="text"
                placeholder="User Cid"
                onChange={(e) => setSearch(e.target.value)}
                value={search}
              />
            </InputGroup>
          </FormControl>

          {isFetching ? (
            <TableSkeleton rowCount={5} />
          ) : userPurchases.length > 0 ? (
            <Card padding={'8px'}>
              <Table color={textColor}>
                <Thead>
                  <Tr>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        User Cid
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Type
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Quantity
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Unit Price
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Gross Amount
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Discount Amount
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Net Amount
                      </Text>
                    </Td>
                    <Td>Purchased At</Td>
                  </Tr>
                </Thead>
                <Tbody>
                  {userPurchases.map((row) => {
                    return (
                      <Tr
                        onClick={() => {
                          if (isUserPurchasesStats || isUserPurchase) {
                            handleEditOpen(row);
                          }
                        }}
                        _last={{
                          '& td': {
                            borderBottom: 'none !important',
                          },
                        }}
                        key={row.id}
                        border="unset"
                        className="cursor-tr"
                      >
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.userCid}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.type}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.quantity}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.unitPrice}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.grossAmount}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.discountAmount}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {row?.netAmount}
                          </Text>
                        </Td>
                        <Td verticalAlign="center" p={paddingTd}>
                          <Text variant="text" color={textColor}>
                            {parseDateTime(row?.purchasedAt)}
                          </Text>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Card>
          ) : (
            <NotFound text="Not Found" />
          )}

          <Pagination
            meta={userPurchasesMeta ?? { limit, offset, total: 0 }}
            isLoadingPage={isFetching}
            currentPage={Math.floor(offset / limit) + 1}
            onPageChange={(newOffset) => setOffset(newOffset)}
          />
        </>
      )}

      {/*<Attention*/}
      {/*  variant="remove"*/}
      {/*  handle={() => handleDelete(object)}*/}
      {/*  isOpen={isOpen}*/}
      {/*  onOpen={onOpen}*/}
      {/*  onClose={onClose}*/}
      {/*/>*/}
    </>
  );
};

export default UserPurchase;
